import { useState, useEffect, useCallback } from 'react';

// Hook for rapid quiz real-time features
export const useRapidQuizRealtime = (sessionId, enabled = true) => {
  const [currentQuestion, setCurrentQuestion] = useState(null);
  const [questionIndex, setQuestionIndex] = useState(0);
  const [rapidStatus, setRapidStatus] = useState('waiting');
  const [participants, setParticipants] = useState([]);
  const [leaderboard, setLeaderboard] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Fetch current question info
  const fetchQuestionInfo = useCallback(async () => {
    if (!sessionId || !enabled) return;
    
    try {
      setLoading(true);
      const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:3001'}/api/live-quiz/rapid/sessions/${sessionId}/question-info`);
      
      if (!response.ok) {
        throw new Error('Failed to fetch question info');
      }
      
      const data = await response.json();
      const questionInfo = data.questionInfo;
      
      setQuestionIndex(questionInfo.current_question_index || 0);
      setRapidStatus(questionInfo.rapid_status || 'waiting');
      
      // Parse quiz data to get current question
      if (questionInfo.quiz_data) {
        const quizData = typeof questionInfo.quiz_data === 'string' 
          ? JSON.parse(questionInfo.quiz_data) 
          : questionInfo.quiz_data;
        
        const currentIndex = questionInfo.current_question_index || 0;
        if (quizData.questions && quizData.questions[currentIndex]) {
          setCurrentQuestion(quizData.questions[currentIndex]);
        }
      }
      
      setError(null);
    } catch (err) {
      console.error('Error fetching question info:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [sessionId, enabled]);

  // Fetch participants
  const fetchParticipants = useCallback(async () => {
    if (!sessionId || !enabled) return;
    
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:3001'}/api/live-quiz/sessions/${sessionId}/participants`);
      
      if (!response.ok) {
        throw new Error('Failed to fetch participants');
      }
      
      const data = await response.json();
      setParticipants(data.participants || []);
    } catch (err) {
      console.error('Error fetching participants:', err);
    }
  }, [sessionId, enabled]);

  // Fetch leaderboard
  const fetchLeaderboard = useCallback(async () => {
    if (!sessionId || !enabled) return;
    
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:3001'}/api/live-quiz/sessions/${sessionId}/leaderboard`);
      
      if (!response.ok) {
        throw new Error('Failed to fetch leaderboard');
      }
      
      const data = await response.json();
      setLeaderboard(data.leaderboard || []);
    } catch (err) {
      console.error('Error fetching leaderboard:', err);
    }
  }, [sessionId, enabled]);

  // Refresh all data
  const refreshAll = useCallback(async () => {
    await Promise.all([
      fetchQuestionInfo(),
      fetchParticipants(),
      fetchLeaderboard()
    ]);
  }, [fetchQuestionInfo, fetchParticipants, fetchLeaderboard]);

  // Auto-refresh when enabled
  useEffect(() => {
    if (!enabled) return;

    // Initial fetch
    refreshAll();

    // Set up interval for real-time updates
    const interval = setInterval(() => {
      refreshAll();
    }, 2000); // Refresh every 2 seconds

    return () => clearInterval(interval);
  }, [enabled, refreshAll]);

  return {
    currentQuestion,
    questionIndex,
    rapidStatus,
    participants,
    leaderboard,
    loading,
    error,
    refreshAll,
    fetchQuestionInfo,
    fetchParticipants,
    fetchLeaderboard
  };
};

// Hook for rapid quiz session management
export const useRapidQuizSession = () => {
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Fetch active rapid quiz sessions
  const fetchSessions = useCallback(async () => {
    try {
      setLoading(true);
      const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:3001'}/api/live-quiz/rapid/active`);
      
      if (!response.ok) {
        throw new Error('Failed to fetch rapid quiz sessions');
      }
      
      const data = await response.json();
      setSessions(data.sessions || []);
      setError(null);
    } catch (err) {
      console.error('Error fetching rapid quiz sessions:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  // Join rapid quiz session
  const joinSession = useCallback(async (sessionId, participantData) => {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:3001'}/api/live-quiz/sessions/${sessionId}/join`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
        },
        body: JSON.stringify(participantData)
      });

      if (!response.ok) {
        throw new Error('Failed to join rapid quiz session');
      }

      const data = await response.json();
      return data;
    } catch (err) {
      console.error('Error joining rapid quiz session:', err);
      throw err;
    }
  }, []);

  // Leave rapid quiz session
  const leaveSession = useCallback(async (sessionId, participantData) => {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:3001'}/api/live-quiz/sessions/${sessionId}/leave`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
        },
        body: JSON.stringify(participantData)
      });

      if (!response.ok) {
        throw new Error('Failed to leave rapid quiz session');
      }

      const data = await response.json();
      return data;
    } catch (err) {
      console.error('Error leaving rapid quiz session:', err);
      throw err;
    }
  }, []);

  // Complete rapid quiz session
  const completeSession = useCallback(async (sessionId, participantData) => {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:3001'}/api/live-quiz/sessions/${sessionId}/complete`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
        },
        body: JSON.stringify(participantData)
      });

      if (!response.ok) {
        throw new Error('Failed to complete rapid quiz session');
      }

      const data = await response.json();
      return data;
    } catch (err) {
      console.error('Error completing rapid quiz session:', err);
      throw err;
    }
  }, []);

  return {
    sessions,
    loading,
    error,
    fetchSessions,
    joinSession,
    leaveSession,
    completeSession
  };
};

